import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxButtonComponent,
  OnyxCheckboxComponent,
  onyxEmailValidator,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToggleComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isEqual } from 'lodash';
import { distinctUntilChanged, map, startWith } from 'rxjs';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';

export type ContractorShippingTypeFormGroup = ReturnType<
  typeof ContractorShippingTypeFormComponent.buildForm
>;
export type ContractorShippingTypeForm = ReturnType<
  ContractorShippingTypeFormGroup['getRawValue']
>;

export type ContractorEmailFormGroup = ReturnType<
  typeof ContractorShippingTypeFormComponent.buildEmailForm
>;

@Component({
  selector: 'app-contractor-shipping-type-form',
  imports: [
    TranslatePipe,
    OnyxToggleComponent,
    OnyxIconButtonComponent,
    OnyxTextFieldComponent,
    OnyxButtonComponent,
    ReactiveFormsModule,
    OnyxIconComponent,
    OnyxCheckboxComponent,
    OnyxIconComponent,
    OnyxTooltipDirective,
  ],
  templateUrl: './contractor-shipping-type-form.component.html',
  styleUrl: './contractor-shipping-type-form.component.scss',
})
export class ContractorShippingTypeFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<ContractorShippingTypeFormGroup>();

  protected readonly I18N = 'contractors.contractorForm.invoiceDocuments';

  protected hasEmails = signal(false);
  protected hasInternalPortal = signal(false);
  protected hasTraditionalMail = signal(false);
  protected isInvalid!: Signal<boolean>;

  public ngOnInit(): void {
    this.form()
      .valueChanges.pipe(
        startWith(undefined),
        map(() => this.form().getRawValue()),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.hasInternalPortal.update(
          (force) =>
            force || Object.values(value.internalPortal).every(Boolean),
        );
        this.hasEmails.set(!!value.emails.length);
        this.hasTraditionalMail.set(!!value.traditionalMail);
      });

    const controls = this.form().controls;
    effect(
      () => {
        ValidationHelper.toggleControls(controls.emails ?? [], {
          enabled: this.hasEmails(),
          reset: true,
        });
        ValidationHelper.toggleControls(controls.internalPortal ?? [], {
          enabled: this.hasInternalPortal(),
          reset: true,
        });
        ValidationHelper.toggleControls(
          controls.isOriginalDocumentRequired ?? [],
          { enabled: this.hasTraditionalMail(), reset: true },
        );

        if (this.hasEmails() && !controls.emails.length) {
          this.addEmail();
        }
      },
      { injector: this.injector },
    );

    effect(
      () =>
        controls.hasExistingType.setValue(
          this.hasEmails() ||
            this.hasInternalPortal() ||
            this.hasTraditionalMail(),
        ),
      { injector: this.injector },
    );

    this.isInvalid = toSignal(
      controls.hasExistingType.statusChanges.pipe(
        map(
          () =>
            controls.hasExistingType.invalid &&
            controls.hasExistingType.touched,
        ),
      ),
      { initialValue: false, injector: this.injector },
    );
  }

  protected addEmail(): void {
    this.form().controls.emails.push(
      ContractorShippingTypeFormComponent.buildEmailForm(this.fb),
    );
  }

  protected removeEmail(index: number): void {
    const emailsControl = this.form().controls.emails;
    emailsControl.removeAt(index);
    if (!emailsControl.length) this.addEmail();
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      emails: fb.array<ContractorEmailFormGroup>([]),
      internalPortal: fb.group({
        url: fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
        login: fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
        password: fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
      }),
      traditionalMail: fb.control<boolean>(false),
      isOriginalDocumentRequired: fb.control<boolean | null>({
        value: null,
        disabled: true,
      }),
      hasExistingType: fb.control(false, [Validators.requiredTrue]),
    });
  }

  public static buildEmailForm(fb: NonNullableFormBuilder) {
    return fb.control<string | null>(null, [
      Validators.required,
      onyxEmailValidator,
    ]);
  }
}
